import React, { useEffect, useRef, useState } from 'react'
import { Box, FormControlLabel, Grid, IconButton, Paper, Switch, TextField, Tooltip, Typography } from '@mui/material'
import CheckIcon from '@mui/icons-material/Check'
import CloseIcon from '@mui/icons-material/Close'
import { Edit } from '@mui/icons-material'
import { useParams } from 'react-router'
import { enqueueSnackbar } from 'notistack'
import { globalGetService, globalPutService } from '../../../utils/globalApiServices'
import { checkApiStatus, getGlobalCookie } from '../../../utils'
import { getManagementFeeConfig, updateManagementFeeConfig } from '../apiService'
import { PageLoader } from '../../../shared_elements'
import { regexConstants } from '../../../constants/regEx'
import ModuleWiseRevisionHistory from '../../../shared_elements/components/ModuleWiseRevisionHistory'
import RevisionHistory from '../../../shared_elements/components/RevisionHistory'
import { useDispatch, useSelector } from 'react-redux'
import { setRevisonHistory } from '../../../shared_elements/actions'
import { trackActivity } from '../../../utils/mixpanel'
import { PermissionWrapper } from '../../../wrappers'

const RENTAL_INVOICE_TYPE_ID = 2
const invoiceTypeOptions = [{ id: RENTAL_INVOICE_TYPE_ID, label: 'Rental' }]

const normalizeInvoiceTypes = (raw) =>
  Array.isArray(raw)
    ? raw.map((item) => Number(typeof item === 'object' ? item?.value : item)).filter((id) => !Number.isNaN(id))
    : []

const parseManagementFeeData = (raw = {}) => {
  return {
    tax_percentage: raw?.tax_percentage ?? '',
    invoice_types: [RENTAL_INVOICE_TYPE_ID],
    is_active: raw?.is_active ?? true,
  }
}

const cloneFeeData = (config) =>
  config ? { ...config, invoice_types: [...(config?.invoice_types || [])] } : config

const ManagementFeeConfiguration = () => {
  const params = useParams()
  const dispatch = useDispatch()
  const { auditLog } = useSelector((state) => state.leases)

  const [isLoading, setLoading] = useState(false)
  const [modeType, setModeType] = useState('view')
  const [error, setError] = useState({})
  const [data, setData] = useState()
  const prevDataRef = useRef()
  const auditNormalizedRef = useRef(false)
  const [revisionHistory, setRevisionHistory] = useState({ modal: false, data: null })

  

  const onFieldChange = (keyParam, value) => {
    setData((prev) => ({ ...(prev || {}), [keyParam]: value }))
  }

  const onRestErrorKey = (keyParam) => {
    setError((prev) => ({ ...prev, [keyParam]: '' }))
  }

  const handleItemClicked = (page_type) => {
    trackActivity('Item Clicked', { page_title: 'Management Fee Configuration', page_type })
  }

  useEffect(() => {
    getManagementFeeConfigData()
  }, [])

  useEffect(() => {
    if (!revisionHistory.modal) {
      auditNormalizedRef.current = false
      return
    }
    if (!auditLog?.length || auditNormalizedRef.current) return
    const flattenVal = (v) =>
      Array.isArray(v) ? v.map((i) => i?.label || i?.name || String(i)).filter(Boolean).join(', ') : v
    const needsFlattening = auditLog.some((entry) =>
      Object.values(entry?.changes || {}).some((c) => Array.isArray(c?.old) || Array.isArray(c?.new))
    )
    if (needsFlattening) {
      auditNormalizedRef.current = true
      dispatch(
        setRevisonHistory(
          auditLog.map((entry) => {
            if (!entry?.changes) return entry
            const changes = {}
            Object.entries(entry.changes).forEach(([key, val]) => {
              changes[key] = { old: flattenVal(val?.old), new: flattenVal(val?.new) }
            })
            return { ...entry, changes }
          })
        )
      )
    } else {
      auditNormalizedRef.current = true
    }
  }, [auditLog, revisionHistory.modal])

  const getManagementFeeConfigData = () => {
    setLoading(true)
    globalGetService(getManagementFeeConfig(params)).then((response) => {
      if (checkApiStatus(response)) {
        setData(parseManagementFeeData(response?.data?.data))
      }
      setLoading(false)
    })
  }

  const onToggleStatus = (checked) => {
    if (checked) {
      const hasValidFee = Number(data?.tax_percentage) > 0
      if (hasValidFee) {
        onFieldChange('is_active', true)
        setLoading(true)
        globalPutService(updateManagementFeeConfig(params), { is_active: true }).then((response) => {
          if (checkApiStatus(response)) {
            enqueueSnackbar(response?.data?.message, { variant: 'success', anchorOrigin: { horizontal: 'right', vertical: 'top' } })
          }
          setLoading(false)
        })
      } else {
        prevDataRef.current = cloneFeeData(data)
        onFieldChange('is_active', true)
        setModeType('edit')
        setError({})
      }
    } else {
      const enableWasPending = modeType === 'edit' && prevDataRef.current?.is_active === false
      if (enableWasPending) {
        setData(cloneFeeData(prevDataRef.current))
        setModeType('view')
        setError({})
      } else {
        const restored = modeType === 'edit' ? cloneFeeData(prevDataRef.current) : data
        setData({ ...(restored || {}), is_active: false })
        setModeType('view')
        setError({})
        setLoading(true)
        globalPutService(updateManagementFeeConfig(params), { is_active: false }).then((response) => {
          if (checkApiStatus(response)) {
            enqueueSnackbar(response?.data?.message, { variant: 'success', anchorOrigin: { horizontal: 'right', vertical: 'top' } })
          }
          setLoading(false)
        })
      }
    }
  }

  const onUpdateManagementFee = () => {
    const taxVal = data?.tax_percentage
    const validationInputs = {
      tax_percentage: !taxVal && taxVal !== 0 ? 'Please enter Management Fee' : Number(taxVal) === 0 ? 'Management Fee % must be greater than 0' : '',
    }
    if (Object.values(validationInputs).every((v) => v === '')) {
      setLoading(true)
      const payload = {
        tax_percentage: Number(data?.tax_percentage),
        invoice_types: normalizeInvoiceTypes(data?.invoice_types),
        is_active: !!data?.is_active,
      }
      globalPutService(updateManagementFeeConfig(params), payload).then((response) => {
        if (checkApiStatus(response)) {
          const saved = parseManagementFeeData({ ...response?.data?.data, is_active: data?.is_active })
          enqueueSnackbar(response?.data?.message, { variant: 'success', anchorOrigin: { horizontal: 'right', vertical: 'top' } })
          setData(saved)
          prevDataRef.current = cloneFeeData(saved)
          setModeType('view')
          setError({})
        } else {
          const errorMsg = response?.data?.message || ''
          if (errorMsg.toLowerCase().includes('tax_percentage')) {
            setError((prev) => ({ ...prev, tax_percentage: 'Management Fee % must be greater than 0' }))
          } else {
            enqueueSnackbar(errorMsg || 'Something went wrong', { variant: 'error', anchorOrigin: { horizontal: 'right', vertical: 'top' } })
          }
        }
        setLoading(false)
      })
    } else {
      setError(validationInputs)
    }
  }


  return (
    <>
      <Paper elevation={0} sx={{ marginTop: '8px', padding: '10px', minHeight: '60px', alignContent: 'center', justifyContent: 'center', boxShadow: '0px 0px 2px 1px #BBBBBB' }}>
        <Grid sx={{ marginBottom: '10px' }}>
          <Typography variant="p">Management Fee Configuration</Typography>
        </Grid>

        <Grid sx={{ mb: 1.5, display: 'flex', alignItems: 'center' }}>
          <PermissionWrapper extraProps={{ application: "lease_revamp", feature: "management_fee_config", operationType: ['U'] }}>
          <FormControlLabel
            labelPlacement="start"
            sx={{ m: 0, pointerEvents: 'none', '& .MuiFormControlLabel-label': { mr: 1.5 }, '& .MuiSwitch-root': { ml: 0.5, pointerEvents: 'auto' } }}
            control={
              <Switch checked={!!data?.is_active} onChange={(e) => onToggleStatus(e.target.checked)} size="small" color="primary" />
            }
            label={
              <Typography variant="body2" sx={{ color: data?.is_active ? 'primary.main' : 'text.secondary' }}>
                Management Fee
              </Typography>
            }
          />
          </PermissionWrapper>
        </Grid>
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ md: 4, xs: 12 }}>
            <TextField
              label="Management Fee %"
              variant="outlined"
              required
              size="small"
              margin="normal"
              fullWidth
              disabled={modeType === 'view'}
              value={data?.tax_percentage || ''}
              error={!!error.tax_percentage}
              helperText={error.tax_percentage || ' '}
              slotProps={{ inputLabel: { shrink: true } }}
              onFocus={() => onRestErrorKey('tax_percentage')}
              onChange={(e) => {
                const value = e.target.value
                if (value === '' || (regexConstants.percantageRegExp.test(value) && !/^100\./.test(value))) {
                  onFieldChange('tax_percentage', value.replace(/^0+(\d)/, '$1'))
                }
              }}
              onBlur={() => {
                const value = String(data?.tax_percentage || '')
                if (value.endsWith('.')) {
                  onFieldChange('tax_percentage', value.slice(0, -1))
                }
              }}
            />
          </Grid>
          <Grid size={{ md: 4, xs: 12 }}>
            <TextField
              label="Applicable Invoice Type"
              variant="outlined"
              size="small"
              margin="normal"
              fullWidth
              disabled
              value={invoiceTypeOptions.find((option) => option?.id === RENTAL_INVOICE_TYPE_ID)?.label || ''}
              helperText=" "
              slotProps={{ inputLabel: { shrink: true } }}
            />
          </Grid>
          <Grid size={{ md: 4, xs: 12 }} className="table-icon-container" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {modeType === 'view' && data?.is_active && (
              <Tooltip title="Edit">
                <Edit
                  onClick={() => { prevDataRef.current = cloneFeeData(data); setModeType('edit'); setError({}); handleItemClicked('Edit Icon - ManagementFeeConfiguration') }}
                  fontSize="small"
                  color="primary"
                  sx={{ cursor: 'pointer', marginBottom: '15px' }}
                />
              </Tooltip>
            )}
            {modeType === 'view' && (
              <Box sx={{ display: 'flex', alignItems: 'center', '& .revision-history': { top: '0 !important', marginBottom: '15px' } }}>
                <Tooltip title="Revision History">
                  <ModuleWiseRevisionHistory
                    ViewGlobalHistory={() => { setRevisionHistory({ modal: true, data: { slug: params?.leaseSlug, type: 'global' } }); handleItemClicked('Revision History Icon - ManagementFeeConfiguration') }}
                  />
                </Tooltip>
              </Box>
            )}
            {modeType === 'edit' && (
              <>
                <Tooltip title="Save">
                  <IconButton
                    color="primary"
                    size="small"
                    onClick={() => { onUpdateManagementFee(); handleItemClicked('Save Icon - ManagementFeeConfiguration') }}
                    sx={{ bgcolor: '#e3f2fd', marginRight: '5px', marginBottom: '15px' }}
                  >
                    <CheckIcon fontSize="small" sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Cancel">
                  <IconButton
                    color="error"
                    size="small"
                    onClick={() => { setData(cloneFeeData(prevDataRef.current)); setModeType('view'); setError({}); handleItemClicked('Cancel Icon - ManagementFeeConfiguration') }}
                    sx={{ bgcolor: '#ffebee', marginBottom: '15px' }}
                  >
                    <CloseIcon fontSize="small" sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              </>
            )}
          </Grid>
        </Grid>
        <Grid sx={{ mb: 0.5, display: 'flex', alignItems: 'center' }}>
          <Typography
            style={{ fontSize: "12px", display: "inline-block", background: "#ffff96", }}
          >
            Note : Please ensure that the ownership for this asset is set to Managed in the Technical Specifications.
          </Typography>
        </Grid>
      </Paper>
      {revisionHistory.modal && (
        <RevisionHistory
          moduleName="management_fee_config"
          revisionHistory={revisionHistory}
          onToggleModal={() => setRevisionHistory({ modal: false, data: null })}
        />
      )}
      {isLoading && <PageLoader />}
    </>
  )
}

export default ManagementFeeConfiguration
